import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import './explorer-sidebar.css';

const minWidth = 160;
const maxWidth = 520;
const memory = new Map<string, number>();
function savedWidth(key: string) {
  try {
    const value = memory.get(key) ?? Number(localStorage.getItem(key));
    return Number.isFinite(value) && value >= minWidth && value <= maxWidth ? value : undefined;
  } catch { return memory.get(key); }
}

export function ExplorerSidebar({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const user = useSyncExternalStore(
    useCallback(listener => client.getQueryCache().subscribe(listener), [client]),
    useCallback(() => client.getQueryData<{ id: number }>(['current-user'])?.id ?? 'guest', [client]),
    () => 'guest' as const,
  );
  return <ResizableSidebar key={user} storageKey={`skrepka-explorer-sidebar-width:${user}`}>{children}</ResizableSidebar>;
}

function ResizableSidebar({ children, storageKey }: { children: ReactNode; storageKey: string }) {
  const pane = useRef<HTMLElement>(null);
  const drag = useRef<{ id: number; x: number; width: number } | null>(null);
  const [width, setWidth] = useState(() => savedWidth(storageKey));
  const [limit, setLimit] = useState(maxWidth);
  const [resizing, setResizing] = useState(false);
  const effective = width === undefined ? undefined : Math.min(width, limit);
  const currentWidth = () => Number.parseFloat(getComputedStyle(pane.current!).width);
  const clamp = (value: number) => Math.round(Math.max(minWidth, Math.min(limit, value)));
  const save = (value: number) => {
    memory.set(storageKey, value);
    try { localStorage.setItem(storageKey, String(value)); } catch { /* Keep the choice for this session. */ }
  };
  useEffect(() => {
    const parent = pane.current?.parentElement;
    if (!parent || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      // Preserve room for the file list even in a narrow modal.
      setLimit(Math.max(minWidth, Math.min(maxWidth, parent.clientWidth - 380)));
    });
    observer.observe(parent);
    return () => observer.disconnect();
  }, []);

  const finish = (id: number, cancelled = false) => {
    if (drag.current?.id !== id) return;
    if (cancelled) setWidth(drag.current.width);
    else save(clamp(currentWidth()));
    drag.current = null;
    setResizing(false);
  };
  return <aside ref={pane} className={`sx-tree sx-tree-resizable${resizing ? ' is-resizing' : ''}`}
    style={{ '--sx-tree-width': effective === undefined ? undefined : `${effective}px` } as React.CSSProperties}>
    {children}
    <div className="sx-tree-resizer" role="separator" tabIndex={0} aria-label="Ширина панели хранилищ"
      aria-orientation="vertical" aria-valuemin={minWidth} aria-valuemax={limit}
      aria-valuenow={effective ?? 230} title="Потяните, чтобы изменить ширину панели хранилищ"
      onPointerDown={event => {
        if (event.button !== 0) return;
        event.preventDefault(); event.stopPropagation();
        drag.current = { id: event.pointerId, x: event.clientX, width: currentWidth() };
        event.currentTarget.setPointerCapture(event.pointerId);
        event.currentTarget.focus(); setResizing(true);
      }}
      onPointerMove={event => {
        if (drag.current?.id !== event.pointerId) return;
        setWidth(clamp(drag.current.width + event.clientX - drag.current.x));
      }}
      onPointerUp={event => finish(event.pointerId)}
      onPointerCancel={event => finish(event.pointerId, true)}
      onLostPointerCapture={event => finish(event.pointerId)}
      onKeyDown={event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault(); event.stopPropagation();
        const next = clamp(event.key === 'Home' ? minWidth : event.key === 'End' ? limit
          : currentWidth() + (event.key === 'ArrowLeft' ? -10 : 10));
        setWidth(next); save(next);
      }} />
  </aside>;
}
